import { useEffect, useId, useRef, useState } from "react";

import { TeacherPlannedLessonsText } from "./TeacherPlannedLessons";

export interface TeacherSelectOption {
  id: string;
  name: string;
  workloadLabel?: string;
  planned_lessons?: number | null;
}

export function TeacherSearchSelect({
  label,
  value,
  options,
  onChange,
  disabled = false,
  placeholder = "请选择教师"
}: {
  label: string;
  value: string;
  options: TeacherSelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}): React.JSX.Element {
  const popupId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = options.find((option) => option.id === value);
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visibleOptions = normalizedQuery
    ? options.filter((option) =>
        [option.name, option.workloadLabel]
          .filter(Boolean)
          .some((text) => text?.toLocaleLowerCase().includes(normalizedQuery))
      )
    : options;

  useEffect(() => {
    if (!open) return;
    function handlePointerDown(event: PointerEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  function close(): void {
    setOpen(false);
    setQuery("");
    triggerRef.current?.focus();
  }

  return (
    <fieldset className="teacher-search-select">
      <legend>{label}</legend>
      <div
        className="multi-select-control"
        ref={rootRef}
        onKeyDown={(event) => {
          if (!open || event.key !== "Escape") return;
          event.preventDefault();
          event.stopPropagation();
          close();
        }}
      >
        <button
          ref={triggerRef}
          className="multi-select-trigger teacher-search-trigger"
          type="button"
          disabled={disabled}
          aria-label={label}
          aria-expanded={open}
          aria-controls={popupId}
          onClick={() => {
            if (open) setQuery("");
            setOpen(!open);
          }}
        >
          <span className={selected ? "" : "placeholder"}>
            {selected?.name ?? placeholder}
          </span>
          <small>{selected ? "已选择" : "输入检索"}</small>
          <span className="multi-select-chevron" aria-hidden="true">⌄</span>
        </button>
        {open ? (
          <div
            id={popupId}
            className="multi-select-popover teacher-search-popover"
          >
            <div className="multi-select-search">
              <input
                type="search"
                value={query}
                aria-label={`搜索${label}`}
                placeholder="输入教师姓名"
                autoFocus
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
            <div className="teacher-search-options" role="listbox" aria-label={`${label}列表`}>
              {visibleOptions.length ? (
                visibleOptions.map((option) => (
                  <button
                    className="teacher-search-option"
                    type="button"
                    role="option"
                    aria-selected={option.id === value}
                    key={option.id}
                    onClick={() => {
                      onChange(option.id);
                      close();
                    }}
                  >
                    <span className="teacher-option-label">
                      <strong className="teacher-option-name">{option.name}</strong>
                      {option.workloadLabel ? (
                        <span className="teacher-option-workload">{option.workloadLabel}</span>
                      ) : null}
                      {option.planned_lessons !== undefined ? (
                        <TeacherPlannedLessonsText value={option.planned_lessons} />
                      ) : null}
                    </span>
                    {option.id === value ? <span aria-hidden="true">✓</span> : null}
                  </button>
                ))
              ) : (
                <p className="multi-select-empty">
                  {options.length ? "没有匹配的教师" : "暂无可选教师"}
                </p>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </fieldset>
  );
}
